import React, { useState, useEffect } from 'react';
import { Mail, Check, Trash2, Eye, EyeOff } from 'lucide-react';
import { getContactMessages, markMessageRead } from '../../services/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await getContactMessages();
      setMessages(res.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await markMessageRead(messageId);
      setMessages(messages.map(m => m.id === messageId ? { ...m, is_read: true } : m));
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark message');
    }
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'unread') return !m.is_read;
    if (filter === 'read') return m.is_read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-teko">Contact Messages</h2>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="px-2 py-1 bg-[var(--primary)] text-white text-xs rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'unread', 'read'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm capitalize ${
              filter === f 
                ? 'bg-[var(--primary)] text-white' 
                : 'bg-[var(--surface)] border border-[var(--border)]'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Messages List */}
      <div className="space-y-4">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={`msg-skeleton-${i}`} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 animate-pulse">
              <div className="h-5 w-1/3 bg-[var(--border)] rounded mb-3" />
              <div className="h-4 w-full bg-[var(--border)] rounded mb-2" />
              <div className="h-4 w-2/3 bg-[var(--border)] rounded" />
            </div>
          ))
        ) : filteredMessages.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-secondary)]">
            <Mail size={48} className="mx-auto mb-4 opacity-50" />
            <p>No messages found</p>
          </div>
        ) : (
          filteredMessages.map(message => (
            <div 
              key={message.id} 
              className={`bg-[var(--surface)] border rounded-xl p-6 ${
                message.is_read ? 'border-[var(--border)]' : 'border-[var(--primary)]/50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium">{message.name}</h3>
                    {!message.is_read && (
                      <span className="w-2 h-2 rounded-full bg-[var(--primary)]" />
                    )}
                  </div>
                  <p className="text-sm text-[var(--text-secondary)]">{message.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--text-secondary)]">
                    {new Date(message.created_at).toLocaleDateString()}
                  </span>
                  {!message.is_read && (
                    <Button variant="ghost" size="sm" onClick={() => handleMarkRead(message.id)}>
                      <Check size={16} />
                    </Button>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <span className="text-sm font-medium text-[var(--primary)]">
                  {message.subject}
                </span>
              </div>

              <p className="text-[var(--text-secondary)]">{message.message}</p>

              <div className="mt-4 pt-4 border-t border-[var(--border)]">
                <a
                  href={`mailto:${message.email}`}
                  className="text-sm text-[var(--primary)] hover:underline"
                >
                  Reply via email
                </a>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminMessages;
